import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal, useNotify } from '@hotjar/hotsauce';

import { disconnect } from '../../domains/slack/api';
import { connect } from '../../domains/slack/connect';
import { useIntegrations } from '../../hooks/use-integrations';
import { IntegrationTokenName } from '../../domains/api';

interface SlackActionsProps {
  siteId: string | null;
  actionLabel: Record<string, string>;
  isAdmin: boolean;
  siteName: string | null;
}

export const SlackActions = ({
  siteId,
  actionLabel,
  isAdmin,
  siteName,
}: SlackActionsProps): JSX.Element => {
  const { t } = useTranslation();
  const { showToast } = useNotify();

  const [isConnected, setIsConnected] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const { integrations, refreshTokens } = useIntegrations(Number(siteId));

  const { isEnabled = false } =
    integrations.find((integration) => integration.tokenName === IntegrationTokenName.Slack) ?? {};

  useEffect(() => {
    setIsConnected(isEnabled);
  }, [isEnabled]);

  const handleConnect = () => {
    if (isConnected) {
      setDisconnectModalOpen(true);
    } else {
      connect(Number(siteId))
        .then(() => {
          // Notify success
          setIsConnected(true);
          refreshTokens();
          showToast({
            text: `Success! Slack is connected to Hotjar for ${siteName}`,
            kind: 'success',
          });
        })
        .catch((error) => {
          // Notify an error.
          showToast({ text: error.message, kind: 'danger' });
        });
    }
  };

  const handleDisconnect = () => {
    // Show a modal prompt asking if they're sure.
    disconnect(Number(siteId)).then(() => {
      setIsConnected(false);
      refreshTokens();
      showToast({
        text: `Slack has been disconnected from Hotjar for ${siteName}`,
        kind: 'success',
      });

      setDisconnectModalOpen(false);
    });
  };

  return (
    <>
      <Button
        aria-label={!isConnected ? t(actionLabel.enable) : t(actionLabel.disable)}
        kind={!isConnected ? 'primary' : 'danger_secondary'}
        isDisabled={!isAdmin}
        onClick={!isConnected ? handleConnect : () => setDisconnectModalOpen(true)}
      >
        {!isConnected ? t(actionLabel.enable) : t(actionLabel.disable)}
      </Button>
      <Modal
        contentLabel={t('integrations:slack.disable-modal.content-label')}
        onClose={() => setDisconnectModalOpen(false)}
        isOpen={disconnectModalOpen}
      >
        <Modal.Header title={t('integrations:slack.disable-modal.title')} />
        <Modal.Body>
          <p>{t('integrations:slack.disable-modal.description', { siteName })}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button kind="ghost" onClick={() => setDisconnectModalOpen(false)}>
            {t('integrations:slack.disable-modal.cancel')}
          </Button>
          <Button kind="danger" onClick={handleDisconnect} type="submit" data-testid="confirm">
            {t('integrations:slack.disable-modal.action')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
